import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useNavigate,
  useLocation,
  useParams,
  Navigate,
} from "react-router-dom";
import { User } from "./types";
import { apiService } from "./services/api";
import { logger } from "./utils/logger";
import { PhantomJobProvider } from "./contexts/PhantomJobContext";
import { ModalProvider, useModal } from "./contexts/ModalContext";
import { useWalkthroughSequence } from "./hooks/useWalkthroughSequence";
import confetti, { type Shape } from "canvas-confetti";
import Auth from "./components/Auth";
import OnboardingForm from "./components/OnboardingForm";
import DataTable from "./components/DataTable";
import FounderProfile from "./components/FounderProfile";
import ActionItems from "./components/ActionItems";
import ProfileSettings from "./components/ProfileSettings";
import Integrations from "./components/Integrations";
import PrivacyPolicy from "./components/PrivacyPolicy";
import TermsOfService from "./components/TermsOfService";
import Pricing from "./components/Pricing";
import SubscriptionManagement from "./components/SubscriptionManagement";
import PlanSelection from "./components/PlanSelection";
import LinkedInConnectionsImport from "./components/LinkedInConnectionsImport";
import FreePlanQRPage from "./components/FreePlanQRPage";
import FreePlanPage from "./components/FreePlanPage";
import TestOnboarding from "./TestOnboarding";
import LandingPage from "./components/LandingPage";
import { ProtectedRoute } from "./components/routing";
import "./App.css";

// Component that handles the main app content with routing
function AppContent() {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [savedOnboardingData, setSavedOnboardingData] = useState<
    | {
        company: string;
        phone: string;
        timezone: string;
        dailySummaryEnabled: boolean;
      }
    | undefined
  >(() => {
    // Load saved data from localStorage on mount
    const saved = localStorage.getItem("onboardingFormData");
    return saved ? JSON.parse(saved) : undefined;
  });
  const [refreshFounders, setRefreshFounders] = useState<
    (() => Promise<void>) | null
  >(null);
  const [isFirstTimeUser, setIsFirstTimeUser] = useState(false);
  const [taskViewMode, setTaskViewMode] = useState<"calendar" | "board">(
    "calendar"
  );
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem("sidebarCollapsed");
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [activeTab, setActiveTab] = useState<{
    id: string;
    name: string;
  } | null>({ id: "default", name: "Table 1" });
  const [customFieldsRefreshTrigger, setCustomFieldsRefreshTrigger] =
    useState(0);

  const navigate = useNavigate();
  const location = useLocation();
  const mouseShapeRef = useRef<Shape | null>(null);

  // Modal management via context
  const { openModal } = useModal();

  // Walkthrough sequence handlers
  const {
    startProductWalkthrough,
    handlePhoneWalkthroughClose,
    handleChromeExtensionClose,
    handleLinkedInImportComplete,
    handleNotionImportComplete,
  } = useWalkthroughSequence({
    user,
    setUser,
    navigate,
    refreshFounders,
    setCustomFieldsRefreshTrigger,
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      mouseShapeRef.current = confetti.shapeFromText({
        text: "🐭",
        scalar: 1.2,
      });
    } catch (error) {
      console.error("Failed to create mouse confetti shape", error);
      mouseShapeRef.current = null;
    }
  }, []);

  // On initial load, fetch current user using cookie-based auth
  useEffect(() => {
    apiService
      .getCurrentUser()
      .then((response) => {
        const userData = response.user || response;
        setUser(userData);
        // Mark token as present (auth is handled via HTTP-only cookie)
        setToken("cookie");

        // Check if this is a first-time user and has active subscription
        const isFirstTime = checkIfFirstTimeUser(userData);
        setIsFirstTimeUser(isFirstTime);

        // If first-time user with active subscription and on people page, start onboarding sequence
        if (
          isFirstTime &&
          hasActiveSubscription(userData) &&
          location.pathname === "/app/people"
        ) {
          // Check if we've already shown the walkthrough (using backend flag)
          if (!userData.walkthroughCompleted) {
            // Small delay to ensure page is fully loaded
            setTimeout(() => {
              startProductWalkthrough();
            }, 300);
          }
        }
      })
      .catch((error) => {
        // 401 is expected when not logged in
        logger.error("Token validation failed:", error);
        setUser(null);
        setToken(null);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [location.pathname]);

  // Refresh user data when returning from successful checkout
  useEffect(() => {
    const checkoutSuccess = location.search.includes("checkout=success");

    if (checkoutSuccess && token) {
      // Clear the checkout=success parameter from the URL immediately to avoid
      // stale state, redirect loops, and incorrect "returning from checkout" detection
      if (typeof window !== "undefined") {
        const params = new URLSearchParams(window.location.search);
        if (params.get("checkout") === "success") {
          params.delete("checkout");
          const newUrl =
            window.location.pathname +
            (params.toString() ? `?${params.toString()}` : "");
          window.history.replaceState({}, "", newUrl);
        }
      }

      // Wait 2 seconds for webhook to process, then refresh user data
      const timer = setTimeout(() => {
        logger.info("Refreshing user data after checkout success");
        apiService
          .getCurrentUser()
          .then((response) => {
            const userData = response.user || response;
            setUser(userData);
            logger.info("User data refreshed:", {
              subscriptionStatus: userData.subscriptionStatus,
            });

            // If user now has an active subscription (Professional plan)
            if (hasActiveSubscription(userData)) {
              // Check if this is a first-time user
              const isFirstTime = checkIfFirstTimeUser(userData);
              setIsFirstTimeUser(isFirstTime);

              // If first-time user, start onboarding sequence (walkthrough → linkedin)
              if (isFirstTime) {
                if (!userData.walkthroughCompleted) {
                  // Navigate to people page and trigger walkthrough
                  navigate("/app/people");
                  // Small delay to ensure navigation completes before showing walkthrough
                  setTimeout(() => {
                    startProductWalkthrough();
                  }, 500);
                } else {
                  // Already seen walkthrough, show LinkedIn import if not imported and not dismissed
                  navigate("/app/people");
                  const isDismissed =
                    localStorage.getItem("linkedInImportDismissed") === "true";
                  if (!userData.connectionsImported && !isDismissed) {
                    setTimeout(() => {
                      openModal('linkedInImport', { onComplete: handleLinkedInImportComplete });
                    }, 300);
                  }
                }
              } else {
                // Not first-time user, normal flow
                navigate("/app/people");
                const isDismissed =
                  localStorage.getItem("linkedInImportDismissed") === "true";
                if (!userData.connectionsImported && !isDismissed) {
                  setTimeout(() => {
                    openModal('linkedInImport', { onComplete: handleLinkedInImportComplete });
                  }, 300);
                }
              }
            }
          })
          .catch((error) => {
            logger.error("Failed to refresh user data after checkout:", error);
          });
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [location.search, token, navigate]);

  // Handle Stripe checkout cancellation redirect
  useEffect(() => {
    const checkoutCanceled = location.search.includes("checkout=canceled");

    if (checkoutCanceled && user && token) {
      // User canceled Stripe checkout - return to plan selection
      navigate("/register/pricing");

      // Clear the checkout parameter from URL to prevent retriggering on refresh
      const params = new URLSearchParams(window.location.search);
      params.delete("checkout");
      const newUrl =
        window.location.pathname +
        (params.toString() ? `?${params.toString()}` : "");
      window.history.replaceState({}, "", newUrl);
    }
  }, [user, token, location.search, navigate]);

  // Handle Outlook connection callback
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const outlookConnected = params.get("outlook_connected");
    const outlookEmail = params.get("outlook_email");

    if (outlookConnected === "true" && outlookEmail && user && token) {
      // Show the email modal
      openModal('outlookEmail', { connectedEmail: decodeURIComponent(outlookEmail) });

      // Clear the URL parameters
      params.delete("outlook_connected");
      params.delete("outlook_email");
      const newUrl =
        window.location.pathname +
        (params.toString() ? `?${params.toString()}` : "");
      window.history.replaceState({}, "", newUrl);
    }
  }, [location.search, user, token, openModal]);

  // Check subscription status - require active subscription or trial
  const hasActiveSubscription = (user: User | null): boolean => {
    if (!user) return false;

    // Allow access if user has active subscription or is in trial period
    return (
      user.subscriptionStatus === "active" ||
      user.subscriptionStatus === "trialing"
    );
  };

  // Check if user has free subscription status
  const hasFreeSubscription = (user: User | null): boolean => {
    if (!user) return false;
    return (
      user.subscriptionStatus === "free" ||
      user.subscriptionStatus === null ||
      user.subscriptionStatus === undefined
    );
  };

  // Check if user just completed checkout
  const isReturningFromCheckout = (): boolean => {
    return location.search.includes("checkout=success");
  };

  const triggerMouseConfetti = useCallback(() => {
    if (typeof window === "undefined") return;

    if (!mouseShapeRef.current) {
      try {
        mouseShapeRef.current = confetti.shapeFromText({
          text: "🐭",
          scalar: 1.2,
        });
      } catch (error) {
        console.error("Failed to create mouse confetti shape", error);
      }
    }

    const shape = mouseShapeRef.current;
    const bursts = 3;
    for (let i = 0; i < bursts; i++) {
      confetti({
        particleCount: 16,
        spread: 65 + Math.random() * 20,
        startVelocity: 35 + Math.random() * 15,
        gravity: 0.8,
        ticks: 220,
        drift: Math.random() * 0.6 - 0.3,
        origin: {
          x: 0.9 + Math.random() * 0.05,
          y: 0.8 + Math.random() * 0.08,
        },
        scalar: 1.1 + Math.random() * 0.2,
        colors: ["#111827", "#4B5563", "#9CA3AF", "#F3F4F6"],
        shapes: shape ? [shape] : undefined,
      });
    }
  }, []);

  const handleAssistantButtonClick = useCallback(() => {
    triggerMouseConfetti();
    // Toggle chat - AppLayout handles the actual open/close via useModal
  }, [triggerMouseConfetti]);

  // Check if user is a first-time user (created within last 10 minutes)
  const checkIfFirstTimeUser = (userData: User): boolean => {
    if (!userData.createdAt) return false;
    const createdAt = new Date(userData.createdAt);
    const now = new Date();
    const tenMinutesAgo = new Date(now.getTime() - 10 * 60 * 1000);
    return createdAt > tenMinutesAgo;
  };

  const handleLogin = (userData: User, authToken: string) => {
    console.log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━");
    console.log("🔐 handleLogin called:", new Date().toISOString());
    console.log("  User data:", {
      id: userData?.id,
      email: userData?.email,
      onboardingCompleted: userData?.onboardingCompleted,
      hasToken: !!authToken,
    });

    setUser(userData);
    // We rely on HTTP-only cookies for auth; token state is just a boolean flag
    setToken("cookie");

    console.log('  User state set, token state set to "cookie"');
    console.log("  Current pathname:", location.pathname);

    // Check if this is a first-time user
    const isFirstTime = checkIfFirstTimeUser(userData);
    setIsFirstTimeUser(isFirstTime);

    if (
      userData.onboardingCompleted === false ||
      userData.onboardingCompleted === undefined
    ) {
      // Navigate to onboarding form URL after registration/login, preserve from parameter if present
      const searchParams = new URLSearchParams(location.search);
      const fromParam = searchParams.get("from");
      navigate(
        `/register/onboarding-form${fromParam ? `?from=${fromParam}` : ""}`
      );
    } else if (hasFreeSubscription(userData)) {
      // Free plan users should see the free plan QR page
      // Use replace: true to prevent back button issues
      navigate("/free-plan", { replace: true });
    } else if (isFirstTime && hasActiveSubscription(userData)) {
      // For first-time users with active subscription, start the onboarding sequence
      // First show the walkthrough
      console.log(
        "  → Navigating to /app/people (first time with subscription)"
      );
      navigate("/app/people");
      setTimeout(() => {
        startProductWalkthrough();
      }, 300);
    } else {
      // Navigate to /app/people page after successful login
      console.log("  → Navigating to /app/people");
      navigate("/app/people");
    }
    console.log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━");
  };

  const handleLogout = () => {
    setUser(null);
    setToken(null);
    // Clear onboarding-related localStorage
    localStorage.removeItem("onboardingFormData");
    // Call backend to clear auth cookie, then navigate to landing
    apiService
      .logout()
      .catch((err) => {
        logger.error("Error during logout:", err);
      })
      .finally(() => {
        // Navigate to landing page and clear URL parameters to prevent redirect loops
        navigate("/", { replace: true });
      });
  };

  const handleShowLinkedInImport = useCallback(() => {
    openModal('linkedInImport', { onComplete: handleLinkedInImportComplete });
  }, [openModal, handleLinkedInImportComplete]);

  const handleShowNotionImport = useCallback((step?: "connect" | "mapping") => {
    openModal('notionImport', {
      initialStep: step,
      onComplete: handleNotionImportComplete
    });
  }, [openModal, handleNotionImportComplete]);

  const handleOnboardingComplete = async (
    updatedUserData?: any,
    formData?: any
  ) => {
    try {
      // Save the form data so it can be restored if user goes back
      if (formData) {
        setSavedOnboardingData(formData);
        // Persist to localStorage so it survives page reloads (e.g., when going to Stripe)
        localStorage.setItem("onboardingFormData", JSON.stringify(formData));
      }

      if (updatedUserData) {
        setUser(updatedUserData);
        // Navigate to free plan page after onboarding, preserve from parameter if present
        const searchParams = new URLSearchParams(location.search);
        const fromParam = searchParams.get("from");
        navigate(`/free-plan${fromParam ? `?from=${fromParam}` : ""}`);
        return;
      }

      const userData = await apiService.getCurrentUser();
      setUser(userData);

      if (userData.onboardingCompleted === true) {
        // Navigate to free plan page after onboarding, preserve from parameter if present
        const searchParams = new URLSearchParams(location.search);
        const fromParam = searchParams.get("from");
        navigate(`/free-plan${fromParam ? `?from=${fromParam}` : ""}`);
      } else {
        // Stay on onboarding form if not completed
        navigate("/register/onboarding-form");
      }
    } catch (err) {
      logger.error("Error refreshing user data:", err);
      navigate("/register/onboarding-form");
    }
  };

  const handlePlanSelectionComplete = async (planId?: string) => {
    // For Free plan, navigate to Free plan QR page
    if (planId === "free") {
      // Use replace: true to remove pricing page from history
      // This prevents back button from Stripe going to pricing page
      navigate("/free-plan", { replace: true });
      return;
    }

    // For other plans, Stripe checkout will handle next step (no navigation needed)
  };

  const handleNavigate = (path: string) => {
    navigate(path);
  };

  const handleFounderClick = (founderId: string) => {
    navigate(`/app/people/${founderId}`);
  };

  const handleRefreshFounders = useCallback(
    (refreshFn: () => Promise<void>) => {
      setRefreshFounders(() => refreshFn);
    },
    []
  );

  const handleProfileClick = () => {
    navigate("/app/profile");
  };

  const handleUserUpdate = (updatedUser: User) => {
    setUser(updatedUser);
  };

  const handleTaskViewModeChange = useCallback(
    (viewMode: "calendar" | "board") => {
      setTaskViewMode(viewMode);
    },
    []
  );

  // Consolidated layout props for ProtectedRoute (simplified - modals now use ModalContext)
  const layoutProps = useMemo(
    () => ({
      user: user!,
      sidebarCollapsed,
      setSidebarCollapsed,
      handleNavigate,
      handleLogout,
      handleProfileClick,
      handleAssistantButtonClick,
      refreshFounders,
      activeTab,
      location,
    }),
    [
      user,
      sidebarCollapsed,
      handleNavigate,
      handleLogout,
      handleProfileClick,
      handleAssistantButtonClick,
      refreshFounders,
      activeTab,
      location,
    ]
  );

  // TEMPORARY: Handle test route
  if (window.location.pathname === "/test-onboarding") {
    return <TestOnboarding />;
  }

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  // Main app rendering with Routes
  return (
    <Routes>
      {/* Public routes - accessible without authentication */}
      <Route
        path="/"
        element={
          <LandingPage
            onGetStarted={() => navigate("/register")}
            onNavigate={handleNavigate}
          />
        }
      />
      <Route
        path="/pricing"
        element={<Pricing onNavigate={handleNavigate} />}
      />
      <Route
        path="/free-plan"
        element={
          user && token ? (
            <FreePlanPage onLogout={handleLogout} />
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />
      <Route
        path="/login"
        element={
          <Auth
            onLogin={handleLogin}
            onNavigate={handleNavigate}
            mode="login"
          />
        }
      />
      <Route path="/auth" element={<Navigate to="/login" replace />} />
      <Route
        path="/register"
        element={
          <Auth
            onLogin={handleLogin}
            onNavigate={handleNavigate}
            mode="register"
          />
        }
      />
      <Route
        path="/privacy-policy"
        element={
          <PrivacyPolicy
            onBack={() => handleNavigate(user ? "/app/people" : "/")}
          />
        }
      />
      <Route
        path="/terms-of-service"
        element={
          <TermsOfService
            onBack={() => handleNavigate(user ? "/app/people" : "/")}
          />
        }
      />

      {/* Registration/Onboarding routes - require authentication but not necessarily subscription */}
      <Route
        path="/register/onboarding-form"
        element={
          user && token ? (
            <OnboardingForm
              onComplete={handleOnboardingComplete}
              initialData={savedOnboardingData}
              userName={user.name}
            />
          ) : (
            (() => {
              console.log(
                "🚫 Route guard blocking /register/onboarding-form:",
                {
                  hasUser: !!user,
                  hasToken: !!token,
                  user: user
                    ? {
                        id: user.id,
                        email: user.email,
                        onboardingCompleted: user.onboardingCompleted,
                      }
                    : null,
                  token,
                  timestamp: new Date().toISOString(),
                  location: window.location.href,
                }
              );
              console.log(
                "  → Redirecting to /register because user or token missing"
              );
              // Add a small delay to see state
              setTimeout(() => {
                console.log("  → After delay, user state:", {
                  hasUser: !!user,
                  hasToken: !!token,
                });
              }, 100);
              return <Navigate to="/register" replace />;
            })()
          )
        }
      />
      <Route
        path="/register/pricing"
        element={
          user && token ? (
            <PlanSelection
              onComplete={handlePlanSelectionComplete}
              onBack={() => {
                // Check if coming from free-plan page
                const searchParams = new URLSearchParams(location.search);
                if (searchParams.get("from") === "free-plan") {
                  navigate("/free-plan");
                } else {
                  navigate("/register/onboarding-form");
                }
              }}
            />
          ) : (
            <Navigate to="/register" replace />
          )
        }
      />
      <Route
        path="/register/free-plan-qr"
        element={
          user && token ? (
            <FreePlanQRPage onBack={() => navigate("/register/pricing")} />
          ) : (
            <Navigate to="/register" replace />
          )
        }
      />

      {/* App routes - require authentication and may require subscription */}
      <Route
        path="/app/linkedin-import"
        element={
          user && token ? (
            // Redirect free users to QR onboarding page
            hasFreeSubscription(user) && !isReturningFromCheckout() ? (
              <Navigate to="/free-plan" replace />
            ) : (
              <LinkedInConnectionsImport
                onComplete={handleLinkedInImportComplete}
              />
            )
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />

      {/* Protected app routes - require authentication and subscription */}
      <Route
        path="/app/people"
        element={
          <ProtectedRoute
            user={user}
            token={token}
            requireSubscription
            checkFreeSubscription
            layoutProps={layoutProps}
            isReturningFromCheckout={isReturningFromCheckout()}
          >
            <DataTable
              onFounderClick={handleFounderClick}
              onRefreshFounders={handleRefreshFounders}
              onActiveTabChange={setActiveTab}
              onShowLinkedInImport={handleShowLinkedInImport}
              onShowNotionImport={handleShowNotionImport}
              user={user!}
              customFieldsRefreshTrigger={customFieldsRefreshTrigger}
            />
          </ProtectedRoute>
        }
      />
      <Route
        path="/app/people/:founderId"
        element={
          <ProtectedRoute
            user={user}
            token={token}
            requireSubscription
            checkFreeSubscription
            layoutProps={layoutProps}
            isReturningFromCheckout={isReturningFromCheckout()}
          >
            <FounderProfileWrapper onBack={() => handleNavigate("/app/people")} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/app/tasks"
        element={
          <ProtectedRoute
            user={user}
            token={token}
            requireSubscription
            checkFreeSubscription
            layoutProps={layoutProps}
            isReturningFromCheckout={isReturningFromCheckout()}
          >
            <ActionItemsWrapper
              onFounderClick={handleFounderClick}
              taskViewMode={taskViewMode}
              onTaskViewModeChange={handleTaskViewModeChange}
            />
          </ProtectedRoute>
        }
      />

      {/* Other authenticated routes - don't require subscription */}
      <Route
        path="/app/profile"
        element={
          <ProtectedRoute
            user={user}
            token={token}
            layoutProps={layoutProps}
            isReturningFromCheckout={isReturningFromCheckout()}
          >
            <ProfileSettings
              user={user!}
              onBack={() => handleNavigate("/app/people")}
              onUserUpdate={handleUserUpdate}
            />
          </ProtectedRoute>
        }
      />
      <Route
        path="/app/integrations"
        element={
          <ProtectedRoute
            user={user}
            token={token}
            layoutProps={layoutProps}
            isReturningFromCheckout={isReturningFromCheckout()}
          >
            <Integrations />
          </ProtectedRoute>
        }
      />
      <Route
        path="/app/pricing"
        element={
          user && token ? (
            <ProtectedRoute
              user={user}
              token={token}
              layoutProps={layoutProps}
              isReturningFromCheckout={isReturningFromCheckout()}
            >
              <Pricing onNavigate={handleNavigate} />
            </ProtectedRoute>
          ) : (
            <Pricing onNavigate={handleNavigate} />
          )
        }
      />
      <Route
        path="/app/subscription"
        element={
          <ProtectedRoute
            user={user}
            token={token}
            layoutProps={layoutProps}
            isReturningFromCheckout={isReturningFromCheckout()}
          >
            <SubscriptionManagement />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

// Wrapper component for FounderProfile that gets the founderId from URL params
function FounderProfileWrapper({ onBack }: { onBack: () => void }) {
  const { founderId } = useParams<{ founderId: string }>();

  if (!founderId) {
    return <div>Founder not found</div>;
  }

  return <FounderProfile founderId={founderId} onBack={onBack} />;
}

// Wrapper component for ActionItems that handles view mode state
function ActionItemsWrapper({
  onFounderClick,
  taskViewMode,
  onTaskViewModeChange,
}: {
  onFounderClick: (founderId: string) => void;
  taskViewMode: "calendar" | "board";
  onTaskViewModeChange: (viewMode: "calendar" | "board") => void;
}) {
  return (
    <ActionItems
      onFounderClick={onFounderClick}
      initialViewMode={taskViewMode}
      onViewModeChange={onTaskViewModeChange}
    />
  );
}

// Main App component that wraps everything in Router
function App() {
  return (
    <Router>
      <PhantomJobProvider>
        <ModalProvider>
          <AppContent />
        </ModalProvider>
      </PhantomJobProvider>
    </Router>
  );
}

export default App;
